import { useMemo, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/currency";
import MoneyAmount from "@/components/MoneyAmount";

const WEEK_LABELS = ["Mon", "Wed", "Fri"]; // sparse labels

/**
 * Spending heatmap — github-style grid of last N weeks.
 * Props: data = [{date, total, count}, ...] ordered oldest -> newest
 */
export default function Heatmap({ data, weeks = 13 }) {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [hover, setHover] = useState(null); // { cell, x, y }
  const wrapRef = useRef(null);

  const { grid, max, totals, monthLabels } = useMemo(() => {
    if (!data?.length) return { grid: [], max: 0, totals: 0, monthLabels: [] };
    const byDate = new Map(data.map((d) => [d.date, d]));
    const today = new Date();
    const dayOfWeek = today.getDay();
    const totalDays = weeks * 7;
    const startOffset = totalDays - 1 - dayOfWeek;
    const start = new Date(today);
    start.setDate(today.getDate() - startOffset);

    const cells = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      const cell = byDate.get(key) || { date: key, total: 0, count: 0 };
      cells.push({ ...cell, _date: new Date(d) });
    }
    const max = Math.max(0, ...cells.map((c) => c.total));
    const cols = [];
    for (let c = 0; c < weeks; c++) {
      cols.push(cells.slice(c * 7, c * 7 + 7));
    }
    const totals = cells.reduce((s, c) => s + c.total, 0);
    // Compute month labels for the top — first cell of each column where month starts
    const labels = [];
    let lastMonth = -1;
    cols.forEach((col, ci) => {
      const m = col[0]?._date?.getMonth();
      if (m !== undefined && m !== lastMonth) {
        labels.push({
          ci,
          label: col[0]._date.toLocaleString(undefined, { month: "short" }),
        });
        lastMonth = m;
      }
    });
    return { grid: cols, max, totals, monthLabels: labels };
  }, [data, weeks]);

  const levelClass = (val) => {
    if (!val) return "";
    const r = val / (max || 1);
    if (r > 0.80) return "phm-l5";
    if (r > 0.55) return "phm-l4";
    if (r > 0.30) return "phm-l3";
    if (r > 0.10) return "phm-l2";
    return "phm-l1";
  };

  const onCellEnter = (e, cell) => {
    if (!wrapRef.current) return;
    const wr = wrapRef.current.getBoundingClientRect();
    const tr = e.currentTarget.getBoundingClientRect();
    setHover({
      cell,
      x: tr.left - wr.left + tr.width / 2,
      y: tr.top - wr.top,
    });
  };

  if (!data?.length) return null;

  return (
    <div className="pcard pcard-hover p-6 md:p-7" data-testid="heatmap-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Spending heatmap</div>
          <div className="font-display text-xl font-bold tracking-tight">Last {weeks} weeks</div>
        </div>
        <div className="text-[12.5px] text-muted-foreground">
          Total: <MoneyAmount value={totals} currency={currency} tone="neutral" className="font-bold text-foreground" />
          {" · "}
          Peak day: <MoneyAmount value={max} currency={currency} tone="neutral" className="font-bold text-foreground" />
        </div>
      </div>

      <div ref={wrapRef} className="relative mt-6 overflow-x-auto pb-1">
        {/* Month labels */}
        <div className="mb-2 ml-7 flex" style={{ gap: 5 }} aria-hidden>
          {grid.map((_, ci) => {
            const lab = monthLabels.find((m) => m.ci === ci);
            return (
              <div key={ci} className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground" style={{ width: 14 }}>
                {lab?.label || ""}
              </div>
            );
          })}
        </div>

        <div className="flex">
          {/* Weekday labels (sparse) */}
          <div className="mr-2 flex flex-col items-end justify-between text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground" style={{ height: 7 * 14 + 6 * 5 }}>
            <span>Sun</span>
            <span>Tue</span>
            <span>Thu</span>
            <span>Sat</span>
          </div>

          {/* Grid */}
          <div className="phm-grid" role="grid" aria-label="Spending heatmap">
            {grid.map((col, ci) => (
              <div key={ci} className="phm-col" role="row">
                {col.map((cell, ri) => (
                  <div
                    key={cell.date}
                    role="gridcell"
                    aria-label={`${cell.date}: ${formatMoney(cell.total, currency)}, ${cell.count} transactions`}
                    className={`phm-cell ${levelClass(cell.total)}`}
                    style={{ animationDelay: `${(ci * 7 + ri) * 8}ms` }}
                    onMouseEnter={(e) => onCellEnter(e, cell)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={(e) => onCellEnter(e, cell)}
                    onBlur={() => setHover(null)}
                    tabIndex={0}
                    data-testid="heatmap-cell"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Premium hover tooltip */}
        {hover && (
          <div
            role="tooltip"
            className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full rounded-xl border border-border/70 bg-white px-3 py-2 text-[12px] text-foreground shadow-[0_18px_36px_-12px_rgba(15,23,42,0.25)] dark:bg-[#0F1411] dark:text-white"
            style={{ left: hover.x, top: hover.y - 8 }}
            data-testid="heatmap-tooltip"
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {new Date(hover.cell.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </div>
            <div className="mt-1 flex items-center gap-3">
              <MoneyAmount value={hover.cell.total} currency={currency} tone="neutral" className="font-bold" />
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">
                {hover.cell.count || 0} txn{hover.cell.count === 1 ? "" : "s"}
              </span>
            </div>
            {/* Arrow */}
            <div
              aria-hidden
              className="absolute left-1/2 top-full -translate-x-1/2 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white dark:border-t-[#0F1411]"
            />
          </div>
        )}

        {/* Legend */}
        <div className="mt-5 flex items-center justify-end gap-2 text-[11px] font-semibold text-muted-foreground">
          <span>Less</span>
          <span className="phm-cell !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span className="phm-cell phm-l1 !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span className="phm-cell phm-l2 !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span className="phm-cell phm-l3 !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span className="phm-cell phm-l4 !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span className="phm-cell phm-l5 !animate-none" style={{ animation: "none", opacity: 1 }} />
          <span>More</span>
        </div>
      </div>
    </div>
  );
}
